import { SerialisationSettingsRequest, SettingsService } from '@src/app/shared/backend/clients';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { CodeGeneratorService } from '@app/shared/services/codeGenerator.service';
import { CodeGenerator, MaskTranslation } from './models/code-generator.model';
import { OrderGenerationAlgorithm } from '@src/app/shared/enums/OrderGenerationAlgorithm.enum';
import { OrderGenerationType } from '@app/shared/enums/OrderGenerationType';
import { OrderOutputFormat } from '@app/shared/enums/OrderOutputFormat';
import { SessionService } from '@src/app/shared/services/session.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-serialization-settings',
    templateUrl: './serialization-settings.component.html',
    styleUrls: ['./serialization-settings.component.scss'],
    host: {
        class: 'app-serialization-settings'
    },
    standalone: false
})
export class SerializationSettingsComponent implements OnInit {

  editingSetting: CodeGenerator | null = null
  generatorForm!: FormGroup;
  maskTranslation: MaskTranslation = {};
  previewText = '';
  serializationSettings: any[] = [];
  loadingSettings = true;
  showCreateForm = false;

  algorithmOptions = Object.values(OrderGenerationAlgorithm)
    .filter(type => type !== OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_LIMITED_EDITION)
    .map(value => ({
    value,
    label: this.getAlgorithmLabel(value),
    description: this.getAlgorithmDescription(value)
  }));

  serializationTypes = Object.values(OrderGenerationType);
  outputFormats = Object.values(OrderOutputFormat);

  serializationTypeOptions: { label: string; value: OrderGenerationType }[] = [];
  outputFormatOptions: { label: string; value: OrderOutputFormat }[] = [];

  private maskNotLongerThanCodeLengthValidator(control: AbstractControl): ValidationErrors | null {
    const algorithm = control.get('algorithm')?.value;
    const mask: string = control.get('mask')?.value;
    const codeLength: number = control.get('codeLength')?.value;

    if (algorithm === OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_CUSTOM && mask && codeLength && mask.length > codeLength) {
      return { maskTooLong: true };
    }

    return null;
  }

  constructor(
    private fb: FormBuilder,
    public translate: TranslateService,
    public codeGeneratorService: CodeGeneratorService,
    private session: SessionService,
    private settingsService: SettingsService
  ) { }

  async ngOnInit(): Promise<void> {
    await this.fetchCompanySettings();
    this.initializeForm();
    this.initializeMaskTranslation();
    this.updatePreview();

    this.generatorForm.get('algorithm')?.valueChanges.subscribe(value => {
      const maskControl = this.generatorForm.get('mask');

      this.initializeMaskTranslation();

      if (value === OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_CUSTOM) {
        maskControl?.setValidators([Validators.required]);
      } else {
        maskControl?.clearValidators();
      }

      maskControl?.updateValueAndValidity();

      this.updatePreview();
    });

    this.generatorForm.get('algorithm')?.valueChanges.subscribe(() => {
      this.generatorForm.updateValueAndValidity();
    });

    this.generatorForm.get('codeLength')?.valueChanges.subscribe(() => {
      this.generatorForm.updateValueAndValidity();
    });

    this.generatorForm.valueChanges.subscribe(() => {
      this.updatePreview();
    });

    this.serializationTypeOptions = this.serializationTypes
      .filter(type => type !== OrderGenerationType.CODE_GENERATION_UNKNOWN)
      .map(type => ({
        label: this.translate.instant(`SYSTEM_SETTINGS.SERIALIZATION.TYPE_${type}`),
        value: type
      }));

    this.outputFormatOptions = this.outputFormats.map(format => ({
      label: this.translate.instant(`SYSTEM_SETTINGS.SERIALIZATION.OUTPUT_FORMAT_${format}`),
      value: format
    }));
  }

  async fetchCompanySettings(forceRefresh = false): Promise<void> {
    this.loadingSettings = true;
    try {
      const settings = await this.session.getCompanySettings(forceRefresh);
      this.serializationSettings = settings.serialisationSettings || [];
    } catch (err) {
      console.error('Failed to fetch company settings', err);
    } finally {
      this.loadingSettings = false;
    }
  }

  toggleCreateForm() {
    this.editingSetting = null;

    this.generatorForm.reset({
      algorithm: OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_ALPHANUMERIC_RANDOM,
      codeLength: null,
      outputFormat:  [],
      type: [],
      mask: 'aaa-bbbb-ccc',
      importDirect: false
    }, { emitEvent: false });

    this.initializeMaskTranslation();
    this.updatePreview();
    this.showCreateForm = true;
  }

  displayCreateFormForMissingSerializationType() {
    this.editingSetting = null;

    this.generatorForm.reset({
      algorithm: OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_ALPHANUMERIC_RANDOM,
      codeLength: null,
      outputFormat: [],
      type: [],
      mask: 'aaa-bbbb-ccc',
      importDirect: false
    }, { emitEvent: false });

    this.serializationTypeOptions = this.getAvailableTypesToCreate();

    this.initializeMaskTranslation();
    this.updatePreview();
    this.showCreateForm = true;
  }

  get presetEntries() {
    return Object.entries(this.codeGeneratorService.presetCharSets).map(([key, value]) => ({ key, value }));
  }

  get maskTranslationKeys(): string[] {
    return Object.keys(this.maskTranslation);
  }

  get showMaskConfiguration(): boolean {
    return this.generatorForm?.get('algorithm')?.value === OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_CUSTOM;
  }

  get showPreview(): boolean {
    return this.maskTranslationKeys.length > 0;
  }

  private initializeForm() {
    this.generatorForm = this.fb.group({
      algorithm: [OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_ALPHANUMERIC_RANDOM, Validators.required],
      codeLength: [null, [Validators.required, Validators.min(1), Validators.max(50)]],
      outputFormat: [[], [Validators.required]],
      type: [[], [Validators.required]],
      mask: ['aa-bbbb-cc', [Validators.required]],
      importDirect: [false]
    }, {
      validators: this.maskNotLongerThanCodeLengthValidator.bind(this)
    });
  }

  public getAvailableTypesToCreate(): { label: string; value: OrderGenerationType }[] {

    const usedTypes = this.serializationSettings.map(setting =>
      this.mapTypeFromBackend(setting.codeType)
    );

    return this.serializationTypes
      .filter(type => type !== OrderGenerationType.CODE_GENERATION_UNKNOWN)
      .filter(type => !usedTypes.includes(type))
      .map(type => ({
        label: this.translate.instant(`SYSTEM_SETTINGS.SERIALIZATION.TYPE_${type}`),
        value: type
      }));
  }

  private initializeMaskTranslation() {
    this.maskTranslation = {
      'a': this.codeGeneratorService.presetCharSets['alphabetic_upper'].values,
      'b': this.codeGeneratorService.presetCharSets['numeric'].values,
      'c': this.codeGeneratorService.presetCharSets['symbols_basic'].values
    };
    this.updatePreview();
  }

  handleMaskTranslationChange(char: string, event: Event) {
    const target = event.target as HTMLTextAreaElement;
    const values = target.value.split(',').map(v => v.trim()).filter(v => v);
    this.maskTranslation = {
      ...this.maskTranslation,
      [char]: values
    };
    this.updatePreview();
  }

  addNewMaskChar() {
    const availableChars = 'abcdefghijklmnopqrstuvwxyz';
    const usedChars = Object.keys(this.maskTranslation);
    const nextChar = availableChars.split('').find(char => !usedChars.includes(char));

    if (nextChar) {
      this.maskTranslation = {
        ...this.maskTranslation,
        [nextChar]: []
      };
      this.updatePreview();
    }
  }

  removeMaskChar(char: string) {
    const newTranslation = { ...this.maskTranslation };
    delete newTranslation[char];
    this.maskTranslation = newTranslation;
    this.updatePreview();
  }

  applyPresetToChar(event: { char: string; presetKey: string }) {
    const preset = this.codeGeneratorService.presetCharSets[event.presetKey];
    if (preset) {
      this.maskTranslation = {
        ...this.maskTranslation,
        [event.char]: preset.values
      };
      this.updatePreview();
    }
  }

  async copyPresetValues(presetKey: string): Promise<void> {
    const preset = this.codeGeneratorService.presetCharSets[presetKey];
    if (preset) {
      await this.codeGeneratorService.copyToClipboard(preset.values.join(', '));
    }
  }

  getPreviewText(char: string): string {
    const values = this.maskTranslation[char] || [];
    const preview = values.slice(0, 10).join(', ');
    return values.length > 10 ? `${preview}...` : preview;
  }

  getPreview(): string {
    const formValue = this.generatorForm.value;
    const generator: CodeGenerator = {
      ...formValue,
      maskTranslation: this.maskTranslation
    };
    return this.codeGeneratorService.generatePreview(generator);
  }

  private updatePreview() {
    this.previewText = this.getPreview();
  }

  public isCustomAlgorithmInvalid(): boolean {
    const algorithm = this.generatorForm.get('algorithm')?.value;
    if (algorithm !== OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_CUSTOM) {
      return false;
    }

    const mask = this.generatorForm.get('mask')?.value?.trim();
    const translationKeys = Object.keys(this.maskTranslation);
    const hasValidCharacterSet = translationKeys.length > 0 &&
      translationKeys.every(char => Array.isArray(this.maskTranslation[char]) && this.maskTranslation[char].length > 0);

    return !mask || !hasValidCharacterSet;
  }

  getMaskTranslationEntries(maskTranslation: Record<string, string[]>) {
    return Object.entries(maskTranslation).map(([char, values]) => ({
      char,
      values,
    }));
  }

  async saveSerializationSetting(): Promise<void> {
    if (this.generatorForm.invalid) {
      return;
    }

    const formValue = this.generatorForm.value;
    const isCustom = formValue.algorithm === OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_CUSTOM;
    const mask = formValue.mask?.trim();

    const translationKeys = Object.keys(this.maskTranslation);
    const hasValidMaskTranslation =
      translationKeys.length > 0 &&
      translationKeys.every(
        char =>
          Array.isArray(this.maskTranslation[char]) &&
          this.maskTranslation[char].length > 0
      );

    if (isCustom && (!mask || !hasValidMaskTranslation)) {
      console.error(
        'A mask and its character sets for each placeholder are required for CUSTOM algorithm.'
      );
      return;
    }

    const payload: SerialisationSettingsRequest = {
      algorithmType: formValue.algorithm,
      codeLength: formValue.codeLength,
      outputFormatType: formValue.outputFormat?.[0],
      codeType: formValue.type?.[0],
       ...(isCustom && { mask: formValue.mask, maskTranslation: this.maskTranslation })
    };

    try {
      if (this.editingSetting) {

        // UPDATE flow
        const updatedSetting: SerialisationSettingsRequest = {
          ...payload
        };

        await firstValueFrom(
          this.settingsService.apiSettingsSerialisationUpdateIdPut(
            this.editingSetting.id,
            updatedSetting
          )
        );
      } else {

        // CREATE flow
        await firstValueFrom(
          this.settingsService.apiSettingsSerialisationCreatePost(payload)
        );
      }

      this.showCreateForm = false;
      this.editingSetting = null;

      // Refresh cached settings so UI is consistent
      await this.fetchCompanySettings(true);

      this.generatorForm.reset({
        algorithm: OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_ALPHANUMERIC_RANDOM,
        codeLength: null,
        outputFormat: [],
        type: [],
        mask: 'aaa-bbbb-ccc',
        importDirect: false
      }, { emitEvent: false });

      this.initializeMaskTranslation();
      this.updatePreview();
    } catch (err) {
      console.error('Failed to save serialization setting:', err);
    }
  }

  editGenerator(setting: CodeGenerator): void {
    this.editingSetting = setting;
    this.showCreateForm = true;

    const algorithmOption = this.algorithmOptions.find(opt => opt.value === setting.algorithmType);

    this.generatorForm.setValue({
      algorithm: algorithmOption?.value ?? null,
      codeLength: setting.codeLength ?? null,
      outputFormat: setting.outputFormatType ? [setting.outputFormatType] : [],
      type: setting.codeType ? [setting.codeType] : [],
      mask: setting.mask ?? 'aaa-bbbb-ccc',
      importDirect: setting.importDirect ?? false
    });

    this.maskTranslation = { ...setting.maskTranslation };
    this.updatePreview();

    // console.log('Saved character sets:', setting.maskTranslation);
    // console.log('Editable character sets:', this.maskTranslation);
    // console.log('Quick presets:', this.presetEntries);
  }

  cancelEdit(): void {
    this.editingSetting = null;
    this.generatorForm.reset({
      algorithm: OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_ALPHANUMERIC_RANDOM,
      codeLength: null,
      outputFormat: [],
      type: [],
      mask: 'aaa-bbbb-ccc',
      importDirect: false
    }, { emitEvent: false });

    this.initializeMaskTranslation();
    this.updatePreview();
    this.showCreateForm = false;
  }

  public mapAlgorithmFromBackend(raw: string): OrderGenerationAlgorithm | null {
    switch (raw) {
      case 'SEQUENTIAL':
        return OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_SEQUENTIAL;
      case 'ALPHANUMERIC_RANDOM':
        return OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_ALPHANUMERIC_RANDOM;
      case 'ISO6461991_RANDOM':
        return OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_ISO6461991_RANDOM;
      case 'CUSTOM':
        return OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_CUSTOM;
      default:
        return null;
    }
  }

  public mapOutputFormatFromBackend(raw: string): OrderOutputFormat | null {
    switch (raw) {
      case 'STANDARD':
        return OrderOutputFormat.CODE_ORDER_OUTPUT_FORMAT_STANDARD;
      case 'WITH_SKU':
        return OrderOutputFormat.CODE_ORDER_OUTPUT_FORMAT_WITH_SKU;
      case 'GS1':
        return OrderOutputFormat.CODE_ORDER_OUTPUT_FORMAT_AGGREGATED_GS1;
      case 'GS1_LINK':
        return OrderOutputFormat.CODE_ORDER_OUTPUT_FORMAT_UNIT_GS1_LINK;
      default:
        return null;
    }
  }

  public mapTypeFromBackend(raw: string): OrderGenerationType | null {
    switch (raw) {
      case 'WARRANTY':
        return OrderGenerationType.CODE_GENERATION_WARRANTY;
      case 'ITEM':
        return OrderGenerationType.CODE_GENERATION_ITEM;
      case 'AGGREGATED_PRODUCT':
        return OrderGenerationType.CODE_GENERATION_AGGREGATED_PRODUCT;
      case 'SECURITY_PASSCODE':
        return OrderGenerationType.CODE_GENERATION_SECURITY_PASSCODE;
      case 'UNKNOWN':
      default:
        return OrderGenerationType.CODE_GENERATION_UNKNOWN;
    }
  }

  public getAlgorithmLabel(value: OrderGenerationAlgorithm | null): string {
    switch (value) {
      case OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_SEQUENTIAL:
        return 'Sequential';
      case OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_ALPHANUMERIC_RANDOM:
        return 'Alphanumeric';
      case OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_ISO6461991_RANDOM:
        return 'ISO Standard';
      case OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_CUSTOM:
        return 'Custom';
      default:
        return '-';
    }
  }

  public getAlgorithmDescription(value: OrderGenerationAlgorithm | null): string {
    switch (value) {
      case OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_SEQUENTIAL:
        return 'Incremental numeric sequence';
      case OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_ALPHANUMERIC_RANDOM:
        return 'Random alphanumeric characters';
      case OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_ISO6461991_RANDOM:
        return 'ISO compliant format';
      case OrderGenerationAlgorithm.CODE_ORDER_GENERATION_ALGORITHM_CUSTOM:
        return 'Custom mask-based generation';
      default:
        return '-';
    }
  }

  public getOutputFormatLabel(value: OrderOutputFormat | null): string {
    switch (value) {
      case OrderOutputFormat.CODE_ORDER_OUTPUT_FORMAT_STANDARD:
        return 'Standard';
      case OrderOutputFormat.CODE_ORDER_OUTPUT_FORMAT_WITH_SKU:
        return 'With SKU';
      case OrderOutputFormat.CODE_ORDER_OUTPUT_FORMAT_AGGREGATED_GS1:
        return 'GS1';
      case OrderOutputFormat.CODE_ORDER_OUTPUT_FORMAT_UNIT_GS1_LINK:
        return 'GS1 Link';
      default:
        return value ?? '-';
    }
  }

  public getTypeLabel(value: OrderGenerationType | null): string {
    switch (value) {
      case OrderGenerationType.CODE_GENERATION_WARRANTY:
        return 'Warranty';
      case OrderGenerationType.CODE_GENERATION_ITEM:
        return 'Item';
      case OrderGenerationType.CODE_GENERATION_AGGREGATED_PRODUCT:
        return 'Aggregated Product';
      case OrderGenerationType.CODE_GENERATION_SECURITY_PASSCODE:
        return 'Security Passcode';
      case OrderGenerationType.CODE_GENERATION_UNKNOWN:
      default:
        return 'Unknown';
    }
  }

}
